import { useState, useEffect, useCallback } from 'react';
import { X, Mail, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

const STORAGE_KEY = 'nf_exit_popup_dismissed';
const DISMISS_DAYS = 14;

export function ExitIntentPopup() {
  const [visible, setVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const isDismissed = useCallback(() => {
    const dismissed = localStorage.getItem(STORAGE_KEY);
    if (!dismissed) return false;
    const dismissedAt = new Date(dismissed);
    const now = new Date();
    const diffDays = (now.getTime() - dismissedAt.getTime()) / (1000 * 60 * 60 * 24);
    return diffDays < DISMISS_DAYS;
  }, []);

  const dismiss = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, new Date().toISOString());
    setVisible(false);
  }, []);

  useEffect(() => {
    if (isDismissed()) return;

    // Delay enabling to avoid triggering on initial load
    const timer = setTimeout(() => {
      const handleMouseLeave = (e: MouseEvent) => {
        if (e.clientY <= 5 && !isDismissed()) {
          setVisible(true);
          document.removeEventListener('mouseout', handleMouseLeave);
        }
      };

      document.addEventListener('mouseout', handleMouseLeave);
      return () => document.removeEventListener('mouseout', handleMouseLeave);
    }, 5000);

    return () => clearTimeout(timer);
  }, [isDismissed]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setSubmitting(true);
    try {
      await supabase.from('newsletter_subscribers').insert({
        email: email.trim().toLowerCase(),
        source: 'exit_intent_popup',
      });
      setSubmitted(true);
      toast.success('Welcome to the Nomad Fest community! 🎉');
      setTimeout(dismiss, 3000);
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300">
      <div className="relative w-full max-w-md rounded-2xl bg-gradient-to-br from-primary/10 via-background to-accent/10 border border-border shadow-2xl p-8">
        <button
          onClick={dismiss}
          className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>

        {submitted ? (
          <div className="text-center py-4">
            <Sparkles className="h-12 w-12 text-primary mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-foreground mb-2">You're in! 🎉</h3>
            <p className="text-muted-foreground">Check your inbox for upcoming Nomad Fest events.</p>
          </div>
        ) : (
          <>
            <div className="text-center mb-6">
              <Mail className="h-10 w-10 text-primary mx-auto mb-3" />
              <h3 className="text-2xl font-bold text-foreground mb-2">
                Don't Miss Out!
              </h3>
              <p className="text-muted-foreground text-sm leading-relaxed">
                Join 5,000+ digital nomads getting first access to festivals, retreats, and exclusive community events worldwide.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-3">
              <Input
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-12 text-base"
                autoFocus
              />
              <Button
                type="submit"
                className="w-full h-12 text-base font-semibold"
                disabled={submitting}
              >
                {submitting ? 'Joining...' : '🌍 Join the Community'}
              </Button>
            </form>

            <p className="text-xs text-muted-foreground text-center mt-4">
              No spam. Unsubscribe anytime. We respect your inbox.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
